import { useMemo, useState } from "react";
import type { EngineHeroProps, EngineModule, InteractiveNode } from "../types";
import { EnergyNode } from "../living/EnergyNode";
import { EnergyChannel } from "../living/EnergyChannel";
import { LivingBackdrop } from "../living/LivingBackdrop";
import { CENTERS, CHANNELS, centerByKey, gatePosition, type CenterDef } from "./bodygraph-data";

type Lens = "overview" | "types" | "authorities" | "profiles" | "centers" | "channels" | "gates";

function Hero({ nodes, onSelect, selectedKey }: EngineHeroProps) {
  const [lens, setLens] = useState<Lens>("overview");
  const [hover, setHover] = useState<string | null>(null);
  const active = hover ?? selectedKey ?? null;

  // Map center key → interactive node coming from the DB (if provided).
  const byKey = useMemo(() => {
    const m = new Map<string, InteractiveNode>();
    for (const n of nodes) m.set(n.key, n);
    return m;
  }, [nodes]);

  const selectedCenter = active ? CENTERS.find((c) => c.key === active) : null;

  const lenses: { id: Lens; label: string }[] = [
    { id: "overview",    label: "Overview" },
    { id: "types",       label: "Types" },
    { id: "authorities", label: "Authorities" },
    { id: "profiles",    label: "Profiles" },
    { id: "centers",     label: "Centers" },
    { id: "channels",    label: "Channels" },
    { id: "gates",       label: "Gates" },
  ];

  return (
    <div className="relative overflow-hidden rounded-2xl border hairline bg-obsidian/60">
      <LivingBackdrop hue={44} density={70} />

      <div className="relative z-10 flex flex-wrap gap-2 border-b hairline p-3">
        {lenses.map((l) => (
          <button key={l.id} type="button"
            onClick={() => setLens(l.id)}
            className={"rounded-full border px-3 py-1 text-xs uppercase tracking-widest transition " +
              (lens === l.id
                ? "border-gold/70 bg-gold/15 text-gold"
                : "hairline text-foreground/70 hover:border-gold/50")}
          >
            {l.label}
          </button>
        ))}
      </div>

      <div className="relative z-10 grid gap-4 p-4 md:grid-cols-[1fr,320px]">
        <div>
          <svg viewBox="0 0 400 640" className="h-auto w-full" role="img"
            aria-label="Human Design bodygraph — living energy centers and channels">

            {/* Channels first so nodes sit on top */}
            {CHANNELS.map((ch) => {
              const a = centerByKey(ch.ends[0])!;
              const b = centerByKey(ch.ends[1])!;
              const isActive = active === a.key || active === b.key;
              const showFlow = lens === "channels" || lens === "overview" || isActive;
              return (
                <EnergyChannel key={ch.gates.join("-")}
                  x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                  hue={a.hue}
                  active={isActive}
                  defined={showFlow}
                  label={`${ch.name} — Gate ${ch.gates[0]}/${ch.gates[1]}`}
                />
              );
            })}

            {/* Centers */}
            {CENTERS.map((c) => {
              const dbNode = byKey.get(c.key);
              const isActive = active === c.key;
              return (
                <g key={c.key}
                  onMouseEnter={() => setHover(c.key)}
                  onMouseLeave={() => setHover(null)}
                  onFocus={() => setHover(c.key)}
                  onBlur={() => setHover(null)}
                >
                  <EnergyNode
                    cx={c.x} cy={c.y} r={c.r} hue={c.hue}
                    label={c.label}
                    active={isActive}
                    defined
                    ariaLabel={`${c.label} center`}
                    onActivate={() => {
                      if (dbNode) onSelect?.(dbNode);
                      else onSelect?.({
                        id: c.key, key: c.key, kind: "region",
                        label: c.label, description: c.summary,
                        payload: { kind: "center", x: c.x, y: c.y },
                        lesson_id: null,
                      });
                    }}
                  />
                  {/* Gates */}
                  {lens === "gates" && c.gates.map((g) => {
                    const p = gatePosition(c, g);
                    return (
                      <g key={g}>
                        <circle cx={p.x} cy={p.y} r={5}
                          fill={`hsl(${c.hue} 85% 60%)`}
                          fillOpacity={0.85}
                          className="motion-safe:animate-[energy-breath_4s_ease-in-out_infinite]"
                          style={{ transformBox: "fill-box", transformOrigin: `${p.x}px ${p.y}px` }}
                        />
                        <text x={p.x} y={p.y + 2} textAnchor="middle"
                          style={{ fontSize: 7 }}
                          className="fill-obsidian pointer-events-none">{g}</text>
                      </g>
                    );
                  })}
                </g>
              );
            })}
          </svg>
        </div>

        <aside className="rounded-xl border hairline bg-obsidian/70 p-4 text-sm">
          {selectedCenter ? (
            <>
              <div className="text-xs uppercase tracking-[0.3em] text-gold">{selectedCenter.label} Center</div>
              <p className="mt-2 font-serif text-lg text-gold-luminous">{selectedCenter.label}</p>
              <p className="mt-2 text-foreground/75">{selectedCenter.summary}</p>
              <div className="mt-3 text-xs text-foreground/60">
                Gates: {selectedCenter.gates.join(" · ")}
              </div>
            </>
          ) : (
            <>
              <div className="text-xs uppercase tracking-[0.3em] text-gold">Living Bodygraph</div>
              <p className="mt-2 text-foreground/75">
                Hover or focus a center to feel its imprint. Switch lenses to
                explore Types, Authorities, Profiles, Centers, Channels and Gates.
              </p>
              <p className="mt-3 text-xs text-foreground/60">
                Motion-safe. Screen-reader labels on every node. Enter/Space to activate.
              </p>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

function LessonAside({ node }: { node: InteractiveNode }) {
  const key = node.key as CenterDef["key"];
  const c = centerByKey(key);
  if (!c) return null;
  return (
    <div className="relative rounded-2xl border hairline bg-obsidian/60 p-4">
      <svg viewBox="0 0 120 120" className="mx-auto h-40 w-40" role="img" aria-label={`${c.label} center`}>
        <EnergyNode cx={60} cy={60} r={44} hue={c.hue} active label={c.label} defined />
      </svg>
      <p className="mt-3 text-center text-xs text-foreground/70">{c.summary}</p>
    </div>
  );
}

export const humanDesignEngine: EngineModule = {
  label: "Human Design — Living Bodygraph",
  Hero,
  LessonAside,
};
